import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Paperclip, Send, X } from 'lucide-react';

interface ChatInputProps {
  onSend: (content: string, files?: File[]) => void;
  disabled?: boolean;
  placeholder?: string;
}

const MAX_FILES = 5;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ACCEPTED_TYPES = '.png,.jpg,.jpeg,.webp,.gif,.pdf,.txt,.csv';

const ChatInput: React.FC<ChatInputProps> = ({ onSend, disabled = false, placeholder = 'Опишите проблему...' }) => {
  const [text, setText] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSend = useCallback(() => {
    if ((!text.trim() && files.length === 0) || disabled) return;
    onSend(text.trim(), files.length > 0 ? files : undefined);
    setText('');
    setFiles([]);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }, [text, files, disabled, onSend]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  const handleTextChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value);
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 96) + 'px'; // max 4 lines
  }, []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(e.target.files || []);
    const valid = newFiles.filter(f => {
      if (f.size > MAX_FILE_SIZE) return false;
      return true;
    });
    setFiles(prev => [...prev, ...valid].slice(0, MAX_FILES));
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const removeFile = useCallback((index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const pastedFiles: File[] = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].kind === 'file') {
        const file = items[i].getAsFile();
        if (file && file.size <= MAX_FILE_SIZE) pastedFiles.push(file);
      }
    }
    if (pastedFiles.length > 0) {
      e.preventDefault();
      setFiles(prev => [...prev, ...pastedFiles].slice(0, MAX_FILES));
    }
  }, []);

  // Stable blob URL map: create once per File, revoke on removal or unmount
  const blobUrlMapRef = useRef<Map<File, string>>(new Map());

  const getPreviewUrl = useCallback((file: File): string | null => {
    if (!file.type.startsWith('image/')) return null;
    const existing = blobUrlMapRef.current.get(file);
    if (existing) return existing;
    const url = URL.createObjectURL(file);
    blobUrlMapRef.current.set(file, url);
    return url;
  }, []);

  // Revoke URLs for removed files and cleanup on unmount
  useEffect(() => {
    const currentFiles = new Set(files);
    for (const [file, url] of blobUrlMapRef.current) {
      if (!currentFiles.has(file)) {
        URL.revokeObjectURL(url);
        blobUrlMapRef.current.delete(file);
      }
    }
  }, [files]);

  useEffect(() => {
    return () => {
      blobUrlMapRef.current.forEach(url => URL.revokeObjectURL(url));
      blobUrlMapRef.current.clear();
    };
  }, []);

  const canSend = (text.trim().length > 0 || files.length > 0) && !disabled;

  return (
    <div className="border-t-gray-200 p-3 bg-surface-white">
      {files.length > 0 && (
        <div className="flex gap-2 mb-2 flex-wrap">
          {files.map((file, i) => (
            <div key={i} className="relative rounded-md border-gray-200 flex items-center justify-center overflow-hidden bg-gray-50" style={{ width: 60, height: 60 }}>
              {getPreviewUrl(file) ? (
                <img
                  src={getPreviewUrl(file)!}
                  alt={file.name}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <span className="text-xs text-gray-500 p-4px text-center break-all">
                  {file.name.slice(0, 12)}
                </span>
              )}
              <button
                type="button"
                onClick={() => removeFile(i)}
                className="absolute w-16px h-16px rounded-full border-none cursor-pointer flex items-center justify-center p-0" style={{ top: 2, right: 2, background: 'rgba(0,0,0,0.5)', color: 'var(--surface-1, white)' }}
              >
                <X size={10} />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-end gap-2">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || files.length >= MAX_FILES}
          style={{
            background: 'none',
            border: 'none',
            cursor: disabled ? 'not-allowed' : 'pointer',
            color: 'var(--gray-400)',
            padding: 'var(--space-1)',
            flexShrink: 0,
          }}
          aria-label="Прикрепить файл"
        >
          <Paperclip size={18} />
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_TYPES}
          multiple
          onChange={handleFileChange}
          className="d-none"
        />
        <textarea
          ref={textareaRef}
          value={text}
          onChange={handleTextChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={10000}
          rows={1}
          className="flex-1 border-gray-200 rounded-md text-sm outline-none overflow-hidden" style={{ resize: 'none', padding: 'var(--space-2) var(--space-3)', lineHeight: 'var(--leading-normal)', fontFamily: 'inherit' }}
          aria-label="Сообщение для AI помощника"
        />
        <button
          type="button"
          onClick={handleSend}
          disabled={!canSend}
          style={{
            background: canSend ? 'var(--primary-600)' : 'var(--gray-200)',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            color: canSend ? 'var(--surface-1, white)' : 'var(--gray-400)',
            cursor: canSend ? 'pointer' : 'not-allowed',
            padding: 'var(--space-2)',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 36,
            height: 36,
            transition: 'background 0.15s',
          }}
          aria-label="Отправить"
        >
          <Send size={16} />
        </button>
      </div>
    </div>
  );
};

export default ChatInput;
